import type { MemberRole } from '@aawas/contracts';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { RealtimeProvider } from './lib/realtime';
import { SessionProvider, useMaybeSession, useRawSession, useSession } from './lib/session';
import { AdminOverview, MembersScreen, NoticesScreen, SettingsScreen } from './screens/admin';
import { ComplaintDetailScreen, ComplaintsScreen, NewComplaintScreen } from './screens/complaints';
import { FinanceScreen, MyDuesScreen } from './screens/finance';
import { SpendingScreen } from './screens/spending';
import { GateLogScreen, GuardConsole } from './screens/guard';
import { Login } from './screens/Login';
import { PlatformScreen } from './screens/platform';
import { Signup } from './screens/Signup';
import { MoreScreen, NotificationsScreen, ProfileScreen, StaffHome, VisitRoute } from './screens/misc';
import { HouseholdScreen, PassesScreen, ResidentHome } from './screens/resident';
import { Shell } from './screens/Shell';
import { VisitorsScreen } from './screens/visits';
import { Button } from './ui';
import { signOut } from './lib/api';

/** Renders children only for the listed roles; anyone else is sent home (server enforces this too). */
function Only({ roles, children }: { roles: MemberRole[]; children: React.ReactNode }) {
  const { current } = useSession();
  return roles.includes(current.role) ? <>{children}</> : <Navigate to="/" replace />;
}

/** One app, many dashboards: the active membership decides what "home" is. */
function Home() {
  const { current } = useSession();
  switch (current.role) {
    case 'admin':
      return <AdminOverview />;
    case 'guard':
      return <GuardConsole />;
    case 'staff':
      return <StaffHome />;
    default:
      return <ResidentHome />;
  }
}

const RESIDENT: MemberRole[] = ['owner', 'tenant', 'family'];

/** The platform operator's area: shown only when the server says this person is the operator (the server enforces it too). */
function PlatformOnly({ children }: { children: React.ReactNode }) {
  const { user } = useSession();
  return user.platformAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

function AuthedRoutes() {
  return (
    <RealtimeProvider>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Home />} />
          <Route path="visitors" element={<Only roles={[...RESIDENT, 'admin']}><VisitorsScreen /></Only>} />
          <Route path="visit/:id" element={<Only roles={[...RESIDENT, 'admin', 'guard']}><VisitRoute /></Only>} />
          <Route path="gate/log" element={<Only roles={['guard']}><GateLogScreen /></Only>} />
          <Route path="passes" element={<Only roles={RESIDENT}><PassesScreen /></Only>} />
          <Route path="household" element={<Only roles={RESIDENT}><HouseholdScreen /></Only>} />
          <Route path="complaints" element={<Only roles={[...RESIDENT, 'admin', 'staff']}><ComplaintsScreen /></Only>} />
          <Route path="complaints/new" element={<Only roles={[...RESIDENT, 'admin']}><NewComplaintScreen /></Only>} />
          <Route path="complaints/:id" element={<Only roles={[...RESIDENT, 'admin', 'staff']}><ComplaintDetailScreen /></Only>} />
          <Route path="notices" element={<NoticesScreen />} />
          <Route path="notifications" element={<NotificationsScreen />} />
          <Route path="finance" element={<Only roles={['admin']}><FinanceScreen /></Only>} />
          <Route path="dues" element={<Only roles={['owner', 'tenant']}><MyDuesScreen /></Only>} />
          <Route path="spending" element={<Only roles={['owner', 'tenant']}><SpendingScreen /></Only>} />
          <Route path="members" element={<Only roles={['admin']}><MembersScreen /></Only>} />
          <Route path="settings" element={<Only roles={['admin']}><SettingsScreen /></Only>} />
          <Route path="platform" element={<PlatformOnly><PlatformScreen /></PlatformOnly>} />
          <Route path="more" element={<MoreScreen />} />
          <Route path="me" element={<ProfileScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </RealtimeProvider>
  );
}

function Gate() {
  const { session } = useRawSession();
  const s = useMaybeSession();
  // Signed out: the public pages. Everything else is the sign-in screen.
  if (!session) {
    return (
      <Routes>
        <Route path="/signup" element={<Signup />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }
  if (!s) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-2xl font-bold">You're not part of a society yet</h1>
        <p className="max-w-sm text-slate-600">Ask your committee to add your mobile number ({session.user.phone}), then sign in again.</p>
        <Button onClick={() => void signOut()}>Sign out</Button>
      </div>
    );
  }
  return <AuthedRoutes />;
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Gate />
      </SessionProvider>
    </BrowserRouter>
  );
}
